import { useTranslations } from 'next-intl';
import { useMutation } from '@apollo/client';

import { CREATE_PAYMENT_INTENT_FOR_EXISTING_PM } from '@monofront/business/queries';
import { ICreatePaymentIntentForExistingPMQueryResult } from '@monofront/types';
import { useCheckoutCartState } from '@monofront/business/contexts';

import { useCheckoutPayment } from '../contexts';
import useCompleteOrder from '../hooks/useCompleteOrder';
import Button from '@monofront/design-system/components/Button';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';

/**
 * Display a Pay CTA to pay with an existing paypal account
 */
const PayWithExistingPaypalCta: React.FC = () => {
  const tCheckout = useTranslations('Checkout');
  const { checkoutCart } = useCheckoutCartState();
  const {
    isOrderValidationInProgress,
    setIsOrderValidationInProgress,
    selectedExistingPaymentMethod,
  } = useCheckoutPayment();
  const { completeOrder } = useCompleteOrder();
  const { handlePaymentError } = useHandlePaymentError();

  const [createPaymentIntentForExistingPM] =
    useMutation<ICreatePaymentIntentForExistingPMQueryResult>(
      CREATE_PAYMENT_INTENT_FOR_EXISTING_PM,
    );

  const validateOrder = async () => {
    setIsOrderValidationInProgress(true);

    try {
      if (
        !checkoutCart?.id ||
        !checkoutCart?.payment?.id ||
        !selectedExistingPaymentMethod
      ) {
        throw new Error('Try to validate a free order with incomplete data.');
      }

      await createPaymentIntentForExistingPM({
        variables: {
          tokenValue: checkoutCart.id,
          cartPaymentId: checkoutCart.payment.id,
          paymentMethodId:
            selectedExistingPaymentMethod.existingPaymentMethodId,
          paymentMethodCode: selectedExistingPaymentMethod.paymentMethodCode,
        },
      });

      await completeOrder();
    } catch (error: any) {
      setIsOrderValidationInProgress(false);

      handlePaymentError({
        code: 'PAY_WITH_EXISTING_PAYPAL_FAILED',
        message:
          !error?.graphQLErrors && error?.message
            ? error.message
            : 'Cannot validate order (pay with existing paypal flow)',
        shouldDisplayError: true,
        error,
        context: {
          selected_pm: selectedExistingPaymentMethod,
        },
      });
    }
  };

  if (!checkoutCart?.id) return null;

  return (
    <Button
      onClick={() => validateOrder()}
      isLoading={isOrderValidationInProgress}
      dataTestId="ActionButtons-order-payment"
    >
      {tCheckout('finishCheckout')}
    </Button>
  );
};

export default PayWithExistingPaypalCta;
